import {
  BASE_USER_CONFIG_API_URL,
  getUserConfigApiUrl,
  markUserConfigExplainerVideoSeen,
  parseUserConfigDto,
  toUserConfigSavePayload,
  type UserConfig
} from '@/entities/user-config';
import { setUser } from '@/services/analytics';
import { AxiosError, http } from '@/services/http';
import { getStagingSuffix } from '@/shared/lib/environmentPaths';

const USER_CONFIG_STORAGE_KEY = 'userConfig';

type SaveUserConfigOptions = {
  userEmail: string;
  previousHomepage: string;
};

const postUserConfig = (userConfig: UserConfig) =>
  http.internal.post(getUserConfigApiUrl(), {
    [USER_CONFIG_STORAGE_KEY]: toUserConfigSavePayload(userConfig)
  });

const isNotFound = (error: unknown): boolean =>
  error instanceof AxiosError && error.response?.status === 404;

const fetchCurrentUserConfig = async (): Promise<UserConfig> => {
  const { data } = await http.internal.get(`${getUserConfigApiUrl()}/${USER_CONFIG_STORAGE_KEY}`);
  return parseUserConfigDto(data);
};

// A brand-new user (just after onboarding, or the first time the `_staging`
// config namespace is used) has no stored config to read or migrate. Seed
// sensible defaults and persist them best-effort so the user can still enter
// the app instead of being blocked on a config fetch that 404s everywhere.
const seedDefaultUserConfig = async (): Promise<UserConfig> => {
  const userConfig = parseUserConfigDto({});
  await postUserConfig(userConfig).catch(() => undefined);
  return userConfig;
};

const migrateUserConfigFromBaseEndpoint = async (): Promise<UserConfig> => {
  try {
    const { data } = await http.internal.get(
      `${BASE_USER_CONFIG_API_URL}/${USER_CONFIG_STORAGE_KEY}`
    );
    const userConfig = parseUserConfigDto(data);
    await postUserConfig(userConfig);
    return userConfig;
  } catch (error) {
    if (isNotFound(error)) return seedDefaultUserConfig();
    throw error;
  }
};

const patchPreferredMarket = (
  userConfig: UserConfig,
  { userEmail, previousHomepage }: SaveUserConfigOptions
): Promise<unknown> | null => {
  const newHomepage = userConfig.homepage.value;

  if (newHomepage === previousHomepage || userEmail.length === 0) {
    return null;
  }

  return http.go.patch(`/users/${userEmail}`, {
    prefMarket: getStagingSuffix(newHomepage)
  });
};

const trackUserConfigSaved = (userConfig: UserConfig) => {
  setUser({
    defaultMarket: userConfig.defaultMarket.label,
    homepage: userConfig.homepage.label
  });
};

export const userConfigApi = {
  fetch: async (): Promise<UserConfig> => {
    try {
      return await fetchCurrentUserConfig();
    } catch (error) {
      if (isNotFound(error)) {
        return migrateUserConfigFromBaseEndpoint();
      }

      throw error;
    }
  },

  save: async (userConfig: UserConfig, saveOptions: SaveUserConfigOptions): Promise<UserConfig> => {
    const preferredMarketPatch = patchPreferredMarket(userConfig, saveOptions);
    const requests = [postUserConfig(userConfig), preferredMarketPatch].filter(
      (request): request is Promise<unknown> => request !== null
    );

    await Promise.all(requests);
    trackUserConfigSaved(userConfig);
    return userConfig;
  },

  markExplainerVideoSeen: async (userConfig: UserConfig): Promise<void> => {
    await postUserConfig(markUserConfigExplainerVideoSeen(userConfig));
  }
};
